import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    LuDatabase,
    LuPenTool,
    LuLanguages,
    LuZap,
    LuUsers,
    LuSearch,
    LuGlobe,
    LuMonitorPlay,
    LuCode,
    LuHeadphones,
    LuChartBar,
    LuPercent,
    LuLightbulb,
    LuShield,
    LuCpu,
    LuCircleCheck,
    LuArrowRight,
    LuSparkles,
    LuCalendar
} from 'react-icons/lu';
import GenAISecurityImage from '../assets/genai-security.jpg';

const GenerativeAI = () => {
    const [hoveredCapability, setHoveredCapability] = useState<number | null>(null);
    const [hoveredValue, setHoveredValue] = useState<number | null>(null);

    return (
        <div>
            {/* Hero Section */}
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-primary py-20 lg:py-32">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <div className="inline-flex items-center space-x-2 bg-blue-800/50 text-blue-100 px-4 py-1.5 rounded-full text-sm font-semibold mb-8 uppercase tracking-wide border border-blue-700">
                        <span>Generative AI for Enterprise</span>
                    </div>
                    <h1 className="text-5xl lg:text-7xl font-bold text-white tracking-tight mb-6">
                        Transforming Concepts into <br />
                        <span className="text-secondary">Scalable Intelligence</span>
                    </h1>
                    <p className="text-xl text-gray-100 max-w-3xl mx-auto mb-10 leading-relaxed">
                        Empowering India's leading organizations with custom LLMs, knowledge-base automation, and generative creative workflows that redefine efficiency.
                    </p>
                    <div className="flex justify-center">
                        <Link
                            to="/contact"
                            className="inline-flex items-center space-x-3 bg-secondary text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-secondary-dark transition-all shadow-lg hover:shadow-secondary/25 group"
                        >
                            <span>Start Your GenAI Journey</span>
                            {React.createElement(LuSparkles as any, { className: "w-5 h-5 group-hover:animate-pulse" })}
                        </Link>
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="py-24 bg-white">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-blue-600 font-semibold tracking-wide uppercase text-sm mb-3">Capabilities</h2>
                        <h3 className="text-4xl font-bold text-slate-900 mb-4">Next-Gen AI Workflows</h3>
                        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                            We build production-ready Generative AI systems that go beyond simple chat interfaces.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: LuDatabase,
                                title: "Custom LLM & RAG Systems",
                                desc: "Build private, secure knowledge bases using your data with Retrieval Augmented Generation (RAG)."
                            },
                            {
                                icon: LuPenTool,
                                title: "Creative Content Automation",
                                desc: "Scale your marketing with AI that generates brand-consistent images, videos, and copy."
                            },
                            {
                                icon: LuLanguages,
                                title: "Indian Language Models",
                                desc: "Fine-tuned models for the Indian market with deep understanding of local dialects and culture."
                            }
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="group relative overflow-hidden bg-white rounded-3xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-100"
                                onMouseEnter={() => setHoveredCapability(index)}
                                onMouseLeave={() => setHoveredCapability(null)}
                                style={{
                                    backgroundImage: hoveredCapability === index
                                        ? `linear-gradient(to bottom right, #2563eb0d, transparent)`
                                        : undefined
                                }}
                            >
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                                        style={{ boxShadow: `0 10px 15px -3px #2563eb33` }}>
                                        {React.createElement(item.icon as any, { className: "w-8 h-8 text-blue-600" })}
                                    </div>
                                    <h4 className="text-xl font-bold text-slate-900 mb-3 transition-colors duration-300"
                                        style={{ color: hoveredCapability === index ? '#2563eb' : undefined }}>
                                        {item.title}
                                    </h4>
                                    <p className="text-slate-600 leading-relaxed">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Value Proposition Section */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-blue-600 font-semibold tracking-wide uppercase text-sm mb-3">Value Proposition</h2>
                        <h3 className="text-4xl font-bold text-slate-900 mb-4">Driving Real Business Impact</h3>
                        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                            Empowering your enterprise with measurable outcomes and strategic advantages.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-5 gap-6">
                        {[
                            { icon: LuZap, title: "70% Faster Content", desc: "Accelerate creative production from weeks to minutes." },
                            { icon: LuUsers, title: "Hyper-Personalized", desc: "Deliver unique experiences to every customer at scale." },
                            { icon: LuSearch, title: "Knowledge Access", desc: "Instant answers to complex enterprise data queries." },
                            { icon: LuGlobe, title: "Multilingual Growth", desc: "Seamless English & Regional Indian market penetration." },
                            { icon: LuMonitorPlay, title: "Creative Scale", desc: "Auto-generate thousands of ad variations instantly." },
                            { icon: LuCode, title: "Dev Velocity", desc: "Faster engineering with AI-assisted development." },
                            { icon: LuHeadphones, title: "Intelligent Support", desc: "Human-like AI that resolves complex customer issues." },
                            { icon: LuChartBar, title: "Synthetic Insights", desc: "Generate data scenarios for better decision making." },
                            { icon: LuPercent, title: "Cost Optimization", desc: "Reduce operational expenses by up to 40% via automation." },
                            { icon: LuLightbulb, title: "Strategic Edge", desc: "Future-proof your business with custom AI IP." },
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="group relative overflow-hidden bg-white rounded-2xl p-6 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                                onMouseEnter={() => setHoveredValue(index)}
                                onMouseLeave={() => setHoveredValue(null)}
                                style={{
                                    backgroundImage: hoveredValue === index
                                        ? `linear-gradient(to bottom right, #2563eb0d, transparent)`
                                        : undefined
                                }}
                            >
                                <div className="relative z-10">
                                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 text-blue-600 group-hover:scale-110 transition-transform duration-300"
                                        style={{ boxShadow: `0 10px 15px -3px #2563eb33` }}>
                                        {React.createElement(item.icon as any, { className: "w-6 h-6" })}
                                    </div>
                                    <h4 className="font-bold text-slate-900 mb-2 transition-colors duration-300"
                                        style={{ color: hoveredValue === index ? '#2563eb' : undefined }}>
                                        {item.title}
                                    </h4>
                                    <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Enterprise Ready Section */}
            <section className="py-24 bg-white overflow-hidden">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-blue-600 font-semibold tracking-wide uppercase text-sm mb-3">Enterprise Ready</h2>
                            <h3 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">Safe, Secure, and Scalable GenAI</h3>
                            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                                We prioritize your data sovereignty and security. Our implementations are designed for Indian regulations and enterprise-grade reliability.
                            </p>

                            <div className="space-y-8 mb-10">
                                {[
                                    { icon: LuShield, title: "Data Privacy", desc: "Your data never leaves your secure cloud environment." },
                                    { icon: LuCpu, title: "Infrastructure", desc: "Optimized for local cloud regions (AWS, Azure, GCP)." },
                                    { icon: LuCircleCheck, title: "Compliance", desc: "Fully aligned with India's DPDP Act & Global Standards." },
                                ].map((item, index) => (
                                    <div key={index} className="flex">
                                        <div className="flex-shrink-0 mt-1">
                                            {React.createElement(item.icon as any, { className: "w-6 h-6 text-slate-900" })}
                                        </div>
                                        <div className="ml-4">
                                            <h4 className="text-lg font-bold text-slate-900">{item.title}:</h4>
                                            <p className="text-slate-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Link
                                to="/contact"
                                className="inline-flex items-center space-x-3 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                            >
                                <span>Request an Audit</span>
                                {React.createElement(LuArrowRight as any, { className: "w-5 h-5" })}
                            </Link>
                        </div>

                        <div className="relative">
                            <div className="relative rounded-3xl overflow-hidden aspect-square shadow-2xl flex items-center justify-center p-10 group">
                                <img
                                    src={GenAISecurityImage}
                                    alt="AI Security"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="relative z-10 glass-card bg-white/10 backdrop-blur-xl border border-white/20 p-8 rounded-2xl text-center shadow-xl">
                                    {React.createElement(LuSparkles as any, { className: "w-12 h-12 text-white mb-4 mx-auto" })}
                                    <div className="text-4xl font-bold text-white mb-2">10x Efficiency</div>
                                    <div className="text-blue-100">Across Workflows</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            {/* CTA Section */}
            <section className="py-14 bg-white border-t border-gray-100 mt-10">
                <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#0B1538] mb-4">
                        Ready to Build the Future?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Leverage the power of Generative AI to stay ahead of the competition.
                    </p>
                    <div className="flex justify-center">
                        <Link
                            to="/contact"
                            className="inline-flex items-center px-8 py-4 bg-[#0B25E6] text-white rounded-lg hover:bg-blue-800  transition-colors text-lg font-bold shadow-lg gap-2"
                        >
                            Schedule a Consultation
                            {React.createElement(LuCalendar as any, { className: "w-5 h-5" })}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default GenerativeAI;
